import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronRight, Folder, FolderUp, Home } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Spinner from '../../../components/ui/Spinner';
import { getErrorMessage } from '../../../lib/apiClient';
import { getContents, moveFile, moveFolder } from '../filesApi';

/**
 * A navigable destination picker. Browse the workspace tree (folders only) and
 * "Move here". Moving a folder into itself is blocked client-side; the backend
 * also rejects cycles.
 */
export default function MoveToFolderModal({ open, onClose, target }) {
  const queryClient = useQueryClient();
  // null = workspace root.
  const [destId, setDestId] = useState(null);
  const [crumbs, setCrumbs] = useState([]); // [{ id, name }]

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['move-picker', destId],
    queryFn: () => getContents(destId ?? undefined, { limit: 100 }),
    enabled: open,
    // The modal stays mounted, so its cache would otherwise survive folders
    // being created/renamed/deleted while it was closed. Always refetch.
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const folders = (data?.folders ?? []).filter((f) => f.id !== target?.item?.id);

  const reset = () => {
    setDestId(null);
    setCrumbs([]);
  };

  const moveMutation = useMutation({
    mutationFn: () =>
      target.type === 'folder'
        ? moveFolder({ id: target.item.id, parentId: destId })
        : moveFile({ id: target.item.id, folderId: destId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
      queryClient.invalidateQueries({ queryKey: ['move-picker'] });
      toast.success('Moved');
      reset();
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not move')),
  });

  const descend = (folder) => {
    setCrumbs((c) => [...c, { id: folder.id, name: folder.name }]);
    setDestId(folder.id);
  };
  const jumpTo = (index) => {
    if (index < 0) {
      reset();
    } else {
      setCrumbs((c) => c.slice(0, index + 1));
      setDestId(crumbs[index].id);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title={target ? `Move “${target.item.name}”` : 'Move'}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
          <button className="inline-flex items-center gap-1 hover:text-brand-600" onClick={() => jumpTo(-1)}>
            <Home className="h-3.5 w-3.5" /> Root
          </button>
          {crumbs.map((c, i) => (
            <span key={c.id} className="inline-flex items-center gap-1">
              <ChevronRight className="h-3 w-3" />
              <button className="hover:text-brand-600" onClick={() => jumpTo(i)}>
                {c.name}
              </button>
            </span>
          ))}
        </div>

        <div className="h-56 overflow-auto rounded-lg border border-slate-200">
          {isLoading || (isFetching && folders.length === 0) ? (
            <div className="py-12">
              <Spinner />
            </div>
          ) : folders.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">No subfolders here.</p>
          ) : (
            folders.map((f) => (
              <button
                key={f.id}
                onClick={() => descend(f)}
                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <Folder className="h-4 w-4 text-amber-500" />
                <span className="flex-1 truncate">{f.name}</span>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </button>
            ))
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">
            Destination: {crumbs.at(-1)?.name ?? 'Root'}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button onClick={() => moveMutation.mutate()} loading={moveMutation.isPending}>
              <FolderUp className="h-4 w-4" /> Move here
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
